'use client'

import { BlobConfig } from '@/lib/features/animatedBackground/animatedBackgroundSlice'
import { createContext, ReactNode, useCallback, useContext, useState } from 'react'

interface AnimatedBackgroundOverride {
  speed?: number
  colors?: BlobConfig[]
}

interface AnimatedBackgroundOverrideContextValue {
  override: AnimatedBackgroundOverride
  setSpeedOverride: (speed: number | undefined) => void
  setColorsOverride: (colors: BlobConfig[] | undefined) => void
}

const AnimatedBackgroundOverrideContext = createContext<
  AnimatedBackgroundOverrideContextValue | undefined
>(undefined)

export function AnimatedBackgroundOverrideProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<AnimatedBackgroundOverride>({})

  const setSpeedOverride = useCallback((speed: number | undefined) => {
    setOverride((prev) => ({ ...prev, speed }))
  }, [])

  const setColorsOverride = useCallback((colors: BlobConfig[] | undefined) => {
    setOverride((prev) => ({ ...prev, colors }))
  }, [])

  return (
    <AnimatedBackgroundOverrideContext.Provider
      value={{ override, setSpeedOverride, setColorsOverride }}
    >
      {children}
    </AnimatedBackgroundOverrideContext.Provider>
  )
}

export function useAnimatedBackgroundOverride() {
  const context = useContext(AnimatedBackgroundOverrideContext)
  // Return empty override if not within provider (makes it optional)
  if (context === undefined) {
    return {
      override: {},
      setSpeedOverride: () => {},
      setColorsOverride: () => {},
    }
  }
  return context
}
